import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { INTEGRATIONS_KEY } from '@account_module/constants/integrationsKeys';
import { fetchIntegration } from '@account_module/services/integrations';
import { Organization } from '@app/types/ADCTypes';
import {
  MEGAPHONE_ONBOARDING_STEPS,
  ONBOARDING_TYPE,
  OnboardingType,
} from '@account_module/constants/onboarding';

// Integrations with the self-service onboarding flow.
const integrationsWithSelfService: OnboardingType[] = [ONBOARDING_TYPE.MEGAPHONE];

export const useCheckIntegration = (organization: Organization | undefined) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const {
    data: integrationData,
    isLoading: isIntegrationLoading,
    error: integrationError,
  } = useQuery({
    queryKey: [INTEGRATIONS_KEY.INTEGRATION_CHECK, organization?.id],
    queryFn: fetchIntegration,
    enabled: !!organization,
    retry: false,
    staleTime: 60 * 60 * 1000, // 1 hour
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const shouldRedirectToOnboarding = useMemo(() => {
    if (!organization) return false;

    if (
      organization?.type &&
      !integrationsWithSelfService.includes(organization.type as OnboardingType)
    ) {
      return false;
    }
    return (
      !integrationData ||
      integrationData?.data.progress_step < Object.keys(MEGAPHONE_ONBOARDING_STEPS).length
    );
  }, [integrationData, isIntegrationLoading, organization]);

  const redirectToOnboarding = (type: string) => navigate(`/account/onboarding/${type}`);

  return {
    isIntegrationLoading: isLoading,
    shouldRedirectToOnboarding,
    integrationError,
    redirectToOnboarding,
  };
};
